import {
  CurrencyEnum,
  DateRangeCustomFilterEnum,
  DateRangePicker,
  FormSelect,
  getDate,
  setDateWithTimezone,
} from '@systemeio/ui'
import AffiliateDashboardInvoices from 'modules/affiliate-dashboard/components/affiliate-dashboard-invoices'
import AffiliateDashboardLeads from 'modules/affiliate-dashboard/components/affiliate-dashboard-leads'
import AffiliateDashboardPayments from 'modules/affiliate-dashboard/components/affiliate-dashboard-payments'
import AffiliateDashboardPromotion from 'modules/affiliate-dashboard/components/affiliate-dashboard-promotion'
import AffiliateDashboardVendorSelector from 'modules/affiliate-dashboard/components/affiliate-dashboard-vendor-selector'
import { AffiliateDashboardLoco } from 'modules/affiliate-dashboard/constants/affiliate-dashboard-loco'
import { useAffiliateDashboardChart } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-chart'
import { useAffiliateDashboardStatistic } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-statistic'
import { useAffiliateDashboardTags } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-tags'
import {
  AffiliateDashboardFilterInterface,
  TagId,
} from 'modules/affiliate-dashboard/types/affiliate-dashboard-interface'
import AffiliateChart from 'modules/affiliate-program/affiliate/components/affiliate-chart'
import AffiliateStatistic from 'modules/affiliate-program/affiliate/components/affiliate-statistic'
import { AffiliateStatisticDateFilterInterface } from 'modules/affiliate-program/affiliate/types/affiliate-interface'
import { isDefaultFilter } from 'modules/affiliate-program/utils/is-default-filter'
import { NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterWithDate } from 'shared/route-query/hooks/use-filter/use-filter-with-date'
import { SelectorDataInterface } from 'shared/types/selector-data-interface'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getCurrencySymbol } from 'shared/utils/get-currency-symbol'
import { getMonthDateRange, getToday } from 'shared/utils/get-dates'

const defaultFilter: AffiliateDashboardFilterInterface = {
  dateTill: null,
  dateSince: null,
  vendorId: undefined,
  currency: undefined,
}

const AffiliateDashboard: NextPage = () => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()

  const [tagsData, setTagsData] = useState<SelectorDataInterface<TagId>[] | undefined>(undefined)

  const { filter, setFilter } = useFilterWithDate(defaultFilter, getMonthDateRange)
  const { data: affiliateTags } = useAffiliateDashboardTags()

  const { data: affiliateStatistic } = useAffiliateDashboardStatistic(filter.vendorId)

  const { data: affiliateChart } = useAffiliateDashboardChart(filter)

  const [currencyData, setCurrencyData] = useState<
    { id: CurrencyEnum; caption: string }[] | undefined
  >()

  useEffect(() => {
    if (affiliateStatistic) {
      const currencies = Object.keys(affiliateStatistic.total) as CurrencyEnum[]
      setCurrencyData(
        currencies.map(el => ({ id: el, caption: getCurrencySymbol(el, intlLocale) })),
      )
    } else {
      setCurrencyData(undefined)
    }
  }, [affiliateStatistic, intlLocale])

  useEffect(() => {
    if (affiliateTags) {
      setTagsData(
        affiliateTags.affiliateTags.map(el => ({
          id: el.id,
          caption: el.name,
        })),
      )
    }
  }, [affiliateTags])

  return (
    <>
      <div className={`grid grid-cols-2 grid-flow-row lg:grid-cols-3 gap-5`}>
        <h1 className={'self-center col-span-2 lg:col-span-1'}>
          {t('header.top_menu.affiliate_dashboard')}
        </h1>
        <div
          className={
            'flex gap-3 justify-end row-start-3 col-span-2 sm:col-span-1 sm:row-start-2 lg:row-start-auto flex-wrap lg:flex-nowrap'
          }
        >
          <DateRangePicker
            disabled={
              !affiliateStatistic || (!isDefaultFilter(filter, user?.timezone) && !affiliateChart)
            }
            getDefaultDate={() => getMonthDateRange(user?.timezone)}
            date={[filter.dateSince, filter.dateTill]}
            maxDate={getToday(user?.timezone)}
            onChange={date => {
              const newFilterState: AffiliateStatisticDateFilterInterface = {
                dateSince: date[0],
                dateTill: date[1],
              }
              setFilter(prev => ({ ...prev, ...newFilterState }))
            }}
            minDate={getDate(user?.affiliateAccountCreatedAt, user?.timezone)}
            customFilters={[
              {
                slug: DateRangeCustomFilterEnum.AllTime,
                handler: () => {
                  const today = setDateWithTimezone('', user?.timezone)
                  today.setHours(23, 59, 59, 999)
                  const firstDate = setDateWithTimezone(
                    user?.affiliateAccountCreatedAt,
                    user?.timezone,
                  )
                  return [firstDate, today]
                },
              },
            ]}
            timezone={user?.timezone}
            createdAt={user?.createdAt}
            intlLocale={intlLocale}
          />
          <FormSelect
            disabled={!affiliateStatistic}
            className={'max-w-[120px]'}
            data={currencyData}
            placeholder={t('global.currency')}
            value={filter.currency}
            onChange={data => setFilter(prev => ({ ...prev, currency: data }))}
          />
        </div>
        <div className={'row-start-2 lg:row-start-auto col-span-2 sm:col-span-1'}>
          <AffiliateDashboardVendorSelector
            selectedVendor={filter.vendorId}
            setFilter={setFilter}
          />
        </div>
        <div className={'flex flex-col gap-5 col-span-2'}>
          <AffiliateChart
            affiliateStatistic={affiliateStatistic}
            selectedCurrency={filter.currency}
            chartData={
              isDefaultFilter(filter, user?.timezone)
                ? affiliateStatistic?.last30Days
                : affiliateChart
            }
          />
          <div>
            <AffiliateDashboardPayments
              setMainFilter={setFilter}
              defaultFilter={defaultFilter}
              mainFilter={filter}
              tagsData={tagsData}
            />
          </div>
          <div>
            <AffiliateDashboardLeads
              setMainFilter={setFilter}
              defaultFilter={defaultFilter}
              mainFilter={filter}
              tagsData={tagsData}
            />
          </div>
          <div>
            <AffiliateDashboardInvoices mainFilter={filter} setMainFilter={setFilter} />
          </div>
        </div>
        <div
          className={
            'flex flex-col gap-5 col-span-2 lg:col-span-1 row-start-4 sm:row-start-3 lg:row-start-auto'
          }
        >
          <AffiliateStatistic
            isDashboard
            selectedCurrency={filter.currency}
            affiliateStatistic={affiliateStatistic}
          />
          <AffiliateDashboardPromotion
            affiliateId={affiliateStatistic?.user.affiliateAccount.affiliateId}
          />
          {/* <AffiliateDashboardOffers vendorId={filter.vendorId} /> */}
        </div>
      </div>
    </>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  AffiliateDashboardLoco,
  'header.top_menu.affiliate_dashboard',
)

export default AffiliateDashboard
